import React from 'react';
import styled from 'styled-components';

import { Heading, Text } from '../../components/typography';
import { useTheme } from '../../themes';
import { withRootTestId } from '../../lib';
import { formatMoneyWithSymbol } from '../../lib/money-formatter';
import { unitsValueMoney } from '../../lib/money';
import { getDeviceInfo } from '../../lib/device-info';
import { Undo } from '../../assets/icons';
import { useCredits, useHasExceededCredits } from './state';
import { convertCashUnitsToRewardUnits, convertRewardUnitsToCashUnits, formatCredits } from './convert-credits';
import { useRewardTerminologyForPlayerCardOperator } from './reward-terminology';
import { IStoreState } from '../../store/types';
import { useSelector } from 'react-redux';

export type Props = {
  inputPreviewValue?: string;
  onDeletePress(): void;
};

const withTestId = withRootTestId('keypad-input');

export default function KeypadInput({ inputPreviewValue = '0', onDeletePress }: Props) {
  const credits = useCredits();
  const theme = useTheme();
  const error = useHasExceededCredits();
  const { unit: rewardUnit } = useRewardTerminologyForPlayerCardOperator();
  const playerCardOperator = useSelector(({ device }: ReturnType<IStoreState>) => device?.info?.playerCardOperator);

  return (
    <Wrapper>
      <Text size="lg" as="h1" weight="bold">
        BET WITH REWARD {rewardUnit.toLocaleUpperCase()}
      </Text>
      <InstructionalText as="p" size="sm">
        Enter the amount to be paid using Reward {rewardUnit}
      </InstructionalText>
      <Input data-testid={withTestId('stake')} as="div" size="2xl" weight="medium">
        <Cash as="p" error={error} size={getStakeFontSize(inputPreviewValue, 5, 6)}>
          ${inputPreviewValue}
        </Cash>
        <span>=</span>
        <Heading as="p" size={getStakeFontSize(inputPreviewValue, 3, 5)}>
          {convertCashUnitsToRewardUnits({ cashUnits: parseFloat(inputPreviewValue) * 100, playerCardOperator })}
          <Text as="sup" weight="regular" size="sm">
            RPs
          </Text>
        </Heading>
        <DeleteIcon onClick={onDeletePress} data-testid={withTestId('delete-button')}>
          <Undo fill={theme.colors.brand.foreground} />
        </DeleteIcon>
      </Input>
      <InstructionalText>
        ($1 = {convertCashUnitsToRewardUnits({ cashUnits: 100, playerCardOperator })} Reward {rewardUnit}. Must apply at
        least {getFormattedMinStake()})
      </InstructionalText>
      <Text as="p" size="md" data-testid={withTestId('available-credits')}>
        <Strong>{formatCredits(credits)}</Strong> Reward {rewardUnit} available{' '}
        <Strong>
          (
          {formatMoneyWithSymbol(
            unitsValueMoney(convertRewardUnitsToCashUnits({ rewardsUnits: credits, playerCardOperator })),
            '$'
          )}
          )
        </Strong>
      </Text>
      <ErrorText show={error}>You don't have enough {rewardUnit.toLocaleLowerCase()} to cover this amount</ErrorText>
    </Wrapper>
  );
}

function getFormattedMinStake() {
  return formatMoneyWithSymbol(unitsValueMoney(getDeviceInfo().minStake), '$');
}

const getStakeFontSize = (stake: string, lowerThreshold: number, upperThreshold: number) => {
  if (stake.length > upperThreshold) {
    return 'lg';
  }
  return stake.length > lowerThreshold ? 'xl' : '2xl';
};

const Strong = styled(Text).attrs(() => ({ as: 'strong', weight: 500 }))``;

const Wrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  margin-bottom: 4rem;
  padding: 0 1.5rem;
  width: 100%;
  * {
    margin: 0;
  }
`;

const InstructionalText = styled(Text)`
  color: ${({ theme }) => theme.components.betWithCredits.instructionalText};
`;

const Input = styled(Heading)`
  align-items: center;
  display: flex;
  column-gap: 0.5rem;
  margin: 2.5rem 0 1.5rem;
  justify-content: center;
  position: relative;
  width: 100%;
`;

const Cash = styled(Heading)<{ error: boolean }>`
  color: ${({ theme, error }) => (error ? theme.colors.status.error : theme.components.betWithCredits.stakeInput)};
  border-bottom: 1px solid
    ${({ theme, error }) => (error ? theme.colors.status.error : theme.components.betWithCredits.stakeInput)};
  text-align: right;
`;

const DeleteIcon = styled.div`
  position: absolute;
  right: 0;
  > svg {
    display: block;
  }
`;

const ErrorText = styled.p<{ show: boolean }>`
  color: ${({ theme }) => theme.colors.status.error};
  visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
`;
